/**
 * The cancel SUB-REASON: why the stated reason is true.
 *
 * The seven cancel reasons record what the customer did ("I have too much
 * product"); they do not say what would fix it. Three customers pick that same
 * reason for three different roots — one over-bought, one uses it less often
 * than it ships, one quietly dislikes it — and only the third is helped by a
 * swap. Routing on the reason alone hands the third customer more of a product
 * she doesn't want, twice, and then lets her cancel.
 *
 * So the sub-reason exists to pick the LEVER, not to fill a dashboard. Each one
 * names the offer that actually answers it. `summary` is written for the model:
 * it is injected verbatim into the prompt as the definition it classifies
 * against, so it must describe the customer's situation, not the remedy.
 *
 * Shared because the widget routes on it and the Next.js backend prompts on it.
 */

export type RetentionLever =
  | "delay"
  | "change_frequency"
  | "swap"
  // A computed size-up bundle: same servings/month, bigger unit, longer interval,
  // lower cost/month. The lead price save when it qualifies (Rose Nectar,
  // single-line) — it precedes the older price levers in both price roots' `leads`
  // so it is reached for FIRST when the widget puts it on the table, and is simply
  // skipped (never invented) when it isn't. See cancel-chat-bundle.ts.
  | "bundle";

export const CANCEL_SUB_REASONS = [
  {
    id: "too_much_stock",
    summary: "they still have unopened product piling up",
    leads: ["delay", "change_frequency"],
  },
  {
    id: "not_using_it",
    summary: "they use it less often than it arrives",
    leads: ["change_frequency", "delay"],
  },
  {
    id: "dislike_product",
    summary: "they don't enjoy the taste, texture, or format",
    leads: ["swap"],
  },
  {
    id: "no_results",
    summary: "they aren't feeling the benefit they hoped for",
    // More time with the same product first (delay + results claim); swap is
    // next if they decline. Dislike language still routes to dislike_product
    // via the prompt ("DISLIKE BEATS"), not here.
    leads: ["delay", "swap"],
  },
  {
    id: "price_vs_value",
    summary:
      "they can afford it but don't think it's worth what they pay — a value judgement, not a budget one",
    leads: ["bundle", "swap", "change_frequency", "delay"],
  },
  {
    id: "budget_tight",
    summary:
      "money is tight right now regardless of what they think it's worth",
    leads: ["bundle", "change_frequency", "delay"],
  },
  {
    id: "life_change",
    summary: "a life event got in the way — moving, travelling, health, a gift",
    leads: ["delay", "change_frequency"],
  },
  {
    id: "buying_elsewhere",
    // Retained ID (kept for analytics continuity); the meaning is now "they'd
    // rather order it themselves, on their own schedule" — the former "or buy it
    // cheaper elsewhere" sense was dropped (an ordinary price complaint).
    summary: "they'd rather order it themselves, on their own schedule",
    leads: ["change_frequency", "delay"],
  },
] as const satisfies ReadonlyArray<{
  id: string;
  summary: string;
  leads: readonly RetentionLever[];
}>;

export type CancelSubReasonId = (typeof CANCEL_SUB_REASONS)[number]["id"];

export const CANCEL_SUB_REASON_IDS = CANCEL_SUB_REASONS.map((s) => s.id) as [
  CancelSubReasonId,
  ...CancelSubReasonId[],
];

export function isCancelSubReasonId(v: unknown): v is CancelSubReasonId {
  return (
    typeof v === "string" &&
    CANCEL_SUB_REASONS.some((s) => (s.id as string) === v)
  );
}

/** The levers that answer this sub-reason, best first. Empty when none fit. */
export function leversForSubReason(
  subReason: CancelSubReasonId | null | undefined,
): readonly RetentionLever[] {
  if (!subReason) return [];
  return CANCEL_SUB_REASONS.find((s) => s.id === subReason)?.leads ?? [];
}

/**
 * Reorder the reason's eligible offers so the sub-reason's levers come first.
 *
 * Only reorders — never adds a lever the reason didn't already allow, and never
 * drops one. Eligibility is a business rule (a pre-2nd-shipment customer may not
 * be offered a swap, no matter what she says); the sub-reason only decides
 * what to reach for FIRST among what is already on the table. Anything not named
 * by the sub-reason keeps its original relative order behind the leads.
 */
export function applySubReasonPriority<T extends string>(
  offers: readonly T[],
  subReason: CancelSubReasonId | null | undefined,
): T[] {
  const leads = leversForSubReason(subReason);
  if (leads.length === 0) return [...offers];
  const promoted = leads.filter((l) => offers.includes(l as unknown as T));
  if (promoted.length === 0) return [...offers];
  const rest = offers.filter((o) => !promoted.includes(o as RetentionLever));
  return [...(promoted as unknown as T[]), ...rest];
}
